import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

/**
 * User information interface for Datadog RUM initialization
 */
export interface DatadogUserInfo {
  firstName?: string;
  lastName?: string;
  userEmail?: string;
  role?: number;
  eventId?: string;
  sessionId?: string;
}

/**
 * Safely check if Datadog RUM is available
 * Returns false if Datadog is not loaded or unavailable
 */
const isDatadogAvailable = (): boolean => {
  return typeof datadogRum !== 'undefined' && 
         typeof datadogRum.init === 'function' &&
         typeof datadogRum.getInitConfiguration === 'function';
};

/**
 * Generate a unique session ID for Datadog tracking
 * Ensures separate instances even when multiple users share the same email
 */
export const generateDatadogSessionId = (
  userEmail: string,
  eventId: string
): string => {
  return `${userEmail || 'unknown'}-${eventId || 'unknown'}-${Date.now()}-${Math.random()
    .toString(36)
    .substring(2, 15)}`;
};

/**
 * Get or create a session ID from sessionStorage
 * Reuses existing session ID if available, otherwise creates a new one
 * Handles cases where sessionStorage might not be available (e.g., private browsing)
 */
export const getOrCreateSessionId = (
  eventId: string,
  userEmail: string
): string => {
  const storageKey = `datadog-session-${eventId}-${userEmail || 'unknown'}`;
  
  try {
    const existingSessionId = sessionStorage.getItem(storageKey);
    
    if (existingSessionId) {
      return existingSessionId;
    }
    
    const newSessionId = generateDatadogSessionId(userEmail, eventId);
    sessionStorage.setItem(storageKey, newSessionId);
    return newSessionId;
  } catch (error) {
    // If sessionStorage is not available (e.g., private browsing), generate a new ID each time
    // This ensures functionality continues even without storage
    console.warn('Datadog RUM: sessionStorage not available, generating new session ID', error);
    return generateDatadogSessionId(userEmail, eventId);
  }
};

/**
 * Parse full name into firstName and lastName
 * Handles cases where name might be in different formats
 */
export const parseUserName = (
  userName: string,
  providedFirstName?: string,
  providedLastName?: string
): { firstName: string; lastName: string } => {
  // Use provided names if available
  if (providedFirstName || providedLastName) {
    return {
      firstName: providedFirstName || '',
      lastName: providedLastName || '',
    };
  }
  
  // Fallback to parsing userName
  const nameParts = (userName || '').trim().split(/\s+/);
  return {
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
  };
};

/**
 * Get Datadog RUM configuration from environment variables
 */
const getDatadogConfig = () => {
  return {
    applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    service: process.env.REACT_APP_DATADOG_SERVICE_NAME || 'engageiq-webinar-frontend',
    env: process.env.REACT_APP_DATADOG_ENV_NAME || 'production',
  };
};

/**
 * Create Datadog RUM initialization configuration
 */
const createInitConfig = (config: ReturnType<typeof getDatadogConfig>) => {
  const initConfig: any = {
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: 'datadoghq.com',
    service: config.service,
    env: config.env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
  };

  // Add React plugin if available
  if (typeof reactPlugin === 'function') {
    initConfig.plugins = [reactPlugin({ router: true })];
  }

  return initConfig;
};

/**
 * Start Datadog RUM session replay and view tracking
 * Wrapped in try-catch to handle account expiration or API errors gracefully
 */
const startDatadogTracking = () => {
  try {
    if (typeof datadogRum.startSessionReplayRecording === 'function') {
      datadogRum.startSessionReplayRecording();
      console.log('Datadog RUM: Session Replay recording started');
    }
  } catch (error) {
    console.warn('Datadog RUM: Failed to start session replay (account may be expired or invalid)', error);
  }

  try {
    if (typeof datadogRum.startView === 'function') {
      datadogRum.startView('producer-dashboard');
    }
  } catch (error) {
    console.warn('Datadog RUM: Failed to start view tracking (account may be expired or invalid)', error);
  }
};

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * This should only be called for Host and Speaker users
 * Fails gracefully if Datadog is unavailable - will not break application functionality
 * @param userInfo Optional user information to set in Datadog RUM
 */
export const initializeDatadogRum = (userInfo?: DatadogUserInfo) => {
  // Early return if Datadog is not available - fail silently
  if (!isDatadogAvailable()) {
    console.warn('Datadog RUM: SDK not available, skipping initialization');
    return;
  }

  try {
    const config = getDatadogConfig();

    console.log('Datadog RUM: Initialization attempt', {
      hasApplicationId: !!config.applicationId,
      hasClientToken: !!config.clientToken,
      service: config.service,
      env: config.env,
    });

    // Validate required environment variables
    if (!config.applicationId || !config.clientToken) {
      console.warn('Datadog RUM: Application ID or Client Token not found. RUM will not be initialized.', {
        applicationId: config.applicationId ? 'present' : 'missing',
        clientToken: config.clientToken ? 'present' : 'missing',
      });
      return;
    }

    // If already initialized, update user context and ensure tracking is active
    if (datadogRum.getInitConfiguration()) {
      console.log('Datadog RUM: Already initialized, updating user context');
      if (userInfo) {
        setUserContext(userInfo);
      }
      startDatadogTracking();
      return;
    }

    // Initialize Datadog RUM with configuration
    const initConfig = createInitConfig(config);
    
    try {
      datadogRum.init(initConfig);
    } catch (initError) {
      // If init fails (e.g., invalid credentials, expired account), log and return
      console.warn('Datadog RUM: Initialization failed (account may be expired or invalid)', initError);
      return; // Exit early - don't continue with user context or tracking
    }

    console.log('Datadog RUM: SDK initialized', {
      applicationId: config.applicationId,
      service: config.service,
      env: config.env,
      sessionReplayEnabled: true,
    });

    // Set user context if provided (wrapped in try-catch for account expiration scenarios)
    if (userInfo) {
      setUserContext(userInfo);
    }

    // Start session replay and view tracking (wrapped in try-catch for account expiration scenarios)
    startDatadogTracking();

    console.log('Datadog RUM initialized successfully, Session Replay started, and view tracking started');
  } catch (error) {
    // Catch any unexpected errors - fail gracefully
    // This handles cases like network errors, API failures, or account expiration
    console.warn('Datadog RUM: Error during initialization (account may be expired or invalid)', error);
    // Don't throw - let the application continue functioning normally
  }
};

/**
 * Get role name from role number
 */
const getRoleName = (role?: number): string => {
  if (role === 1) return 'Host';
  if (role === 0) return 'Speaker';
  return 'Unknown';
};

/**
 * Construct full name from firstName and lastName
 */
const constructFullName = (firstName?: string, lastName?: string): string => {
  const fullName = [firstName, lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || 'Unknown User';
};

/**
 * Generate unique user ID for Datadog tracking
 * Uses sessionId if provided, otherwise generates a new one
 */
const getUniqueUserId = (userInfo: DatadogUserInfo): string => {
  if (userInfo.sessionId) {
    return userInfo.sessionId;
  }
  return generateDatadogSessionId(
    userInfo.userEmail || 'unknown',
    userInfo.eventId || 'unknown'
  );
};

/**
 * Set user context in Datadog RUM for better session tracking
 * Fails gracefully if Datadog methods are unavailable or account is expired
 * Wrapped in try-catch to handle account expiration, network errors, or API failures
 */
const setUserContext = (userInfo: DatadogUserInfo) => {
  if (!isDatadogAvailable()) {
    return; // Silently fail if Datadog is not available
  }

  try {
    const roleName = getRoleName(userInfo.role);
    const fullName = constructFullName(userInfo.firstName, userInfo.lastName);
    const uniqueUserId = getUniqueUserId(userInfo);

    // Set user information
    if (typeof datadogRum.setUser === 'function') {
      try {
        datadogRum.setUser({
          id: uniqueUserId,
          name: fullName,
          email: userInfo.userEmail,
        });
      } catch (error) {
        console.warn('Datadog RUM: Failed to set user (account may be expired or invalid)', error);
        return; // Exit early if setUser fails (likely account issue)
      }
    }

    // Add custom attributes for filtering and analysis
    if (typeof datadogRum.setGlobalContextProperty === 'function') {
      try {
        datadogRum.setGlobalContextProperty('userRole', roleName);
        datadogRum.setGlobalContextProperty('firstName', userInfo.firstName || '');
        datadogRum.setGlobalContextProperty('lastName', userInfo.lastName || '');
        datadogRum.setGlobalContextProperty('eventId', userInfo.eventId || '');
        datadogRum.setGlobalContextProperty('sessionId', uniqueUserId);
      } catch (error) {
        console.warn('Datadog RUM: Failed to set global context properties (account may be expired or invalid)', error);
      }
    }
  } catch (error) {
    // Catch any unexpected errors - fail gracefully
    console.warn('Datadog RUM: Error setting user context (account may be expired or invalid)', error);
    // Don't throw - let the application continue functioning
  }
};

